//react
import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
// prime react
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
// components
import Panel from "../../../components/panel/panel";
import Input from "../../../components/input/Input";
import Select from "../../../components/input/Select";
import CalendarInput from "../../../components/input/CalendarInput";
//pages
import { AccountHeaderToPay } from "./AccountHeaderToPay";
import { TableAccountToPay } from "./TableAccountToPay";
//utils
import { formatYearDate } from "../../../utils/formatDate";
import { formatCurrency } from "../../../utils/formatCurrency";
//services
import { getCostCenter } from "../../../services/CostCenter/getCostCenter";
import { getAccountToPay } from "../../../services/Financial/AccountToPay/getAccountToPay";
//hooks
import { useOptionsSelect } from "../../../hooks/useOptionsSelect";
//contextos
import { useFinancialContext } from "../../../context/FinancialContext";
import { useAppContext } from "../../../context/AppContext";

export default function AccountToPay() {
  const form = useForm();
  const errors = form.formState.errors;
  const [loading, setLoading] = useState(false);
  const { nomenclatures, permissions } = useAppContext();
  const [dataTable, setDataTable] = useState([]);
  const [totals, setTotals] = useState({});
  const [enterpriseLogoBase64, setEnterpriseLogoBase64] = useState(null);
  const nomenclature = JSON.parse(nomenclatures);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [cost_center, setCostCenter] = useState(null);

  const { filter, setFilter, savedDataTable, setSavedDataTable } =
    useFinancialContext();

  const { data, isLoading } = useQuery({
    queryKey: ["cost-center"],
    queryFn: () => getCostCenter(),
    enabled: !!permissions?.includes("116"),
  });

  const {
    supplier,
    isLoadingSupplier,
    enterprise,
    isLoadingEnterprises,
    selectEnterprise,
    blocks,
    isLoadingBlocks,
    selectBlock,
    lands,
    isLoadingLands,
    typePeriodOptions,
    currentAccount,
  } = useOptionsSelect();

  const optionCurrent = [
    { value: 0, label: "Em Aberto" },
    { value: 1, label: "Pago" },
  ];

  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);


  const { 
  dataAccountToPay, 
  isLoadingAccountToPay, 
  isFetching 
} = useQuery({
  queryKey: ['contasAPagar', filters, page],
  
  queryFn: () => getAccountToPay(filters, page),
  
  enabled: Object.keys(filters).length > 0,
  
  keepPreviousData: true, 
  
  onError: (error) => {
    toast.error("Falha ao realizar consulta!");
    console.error(error);
  },
});


  //functions
  const onSubmit = async (data) => {
    setLoading(true);

    if (data.status === "all") delete data.status;

    if (data.period_start && data.period_end) {
      data.period_start = formatYearDate(data.period_start);
      data.period_end = formatYearDate(data.period_end);
    }

    const cleanedData = {};
    for (let key in data) {
      if (data[key] !== undefined && data[key] !== "") {
        cleanedData[key] = data[key];
      }
    }

    const notification = toast.loading(
      "Por favor aguarde a consulta está sendo realizada..."
    );

    setFilter(cleanedData);

    try {
  const response = await getAccountToPay(data);
      // console.log("contas a pagar", response);

      //mapeando (primereact) o objeto recebido pelo endpoint getAccountToPay
      const obj = (response.installments || []).map((r) => ({
        ...r,
        block: r?.block?.name ? r?.block?.name + " / " + r?.land?.name : "",
      }));
      setDataTable(obj);
      setSavedDataTable(obj);

      setTotals(response.totals || {});
      // Try to capture enterprise logo from response (similar to receivables)
      const logoBase64 =
        response?.enterprise_logo_base64 ||
        response?.enterprise_logo ||
        response?.logo_base64 ||
        response?.logo ||
        null;
      setEnterpriseLogoBase64(logoBase64);

      const total = response.last_page * response.per_page;
      setTotalRecords(total);
      setCurrentPage(response.current_page);
      setFirst(0);
      setRows(response.per_page);

      if (obj.length === 0) {
        toast.update(notification, {
          render: "Nenhum resultado para a consulta realizada",
          type: "warning",
          theme: "colored",
          autoClose: 5000,
          isLoading: false,
        });
      } else {
        toast.update(notification, {
          render: "Consulta realizada com sucesso",
          type: "success",
          theme: "colored",
          autoClose: 5000,
          isLoading: false,
        });
      }
    } catch (error) {
      toast.update(notification, {
        render: "Falha ao realizar consulta!",
        type: "error",
        theme: "colored",
        autoClose: 5000,
        isLoading: false,
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  };


const pageTotals = useMemo(() => {
  if (!dataTable || dataTable.length === 0) {
    return {
      totalValue: 0,
      totalPayment: 0,
      totalAddition: 0,
      totalDiscount: 0,
    };
  }

  return dataTable.reduce(
    (acc, item) => {
      acc.totalValue += parseFloat(item.value) || 0;
      acc.totalPayment += parseFloat(item.payment_value) || 0;
      acc.totalAddition += parseFloat(item.addition) || 0;
      acc.totalDiscount += parseFloat(item.discount) || 0;
      return acc;
    },
    {
      totalValue: 0,
      totalPayment: 0,
      totalAddition: 0,
      totalDiscount: 0,
    }
  );
}, [dataTable]);

  async function updatedTable() {
    const response = await getAccountToPay(filter);

    // console.log("contas a pagar update", response);

    const dataResponse = response;

    let obj = [];

    Object.keys(dataResponse)?.forEach((item) => {
      if (dataResponse[item]) {
        obj.push({
          ...dataResponse[item],
          block: dataResponse[item]?.sale?.land?.name
            ? dataResponse[item]?.sale?.land?.name +
              " / " +
              dataResponse[item].sale?.land?.block?.name
            : "",
        });
      }
    }); // array of objects

    // toast.success("Informações atualizadas!");
    setDataTable(obj);
    setLoading(false);
  }

  const handlePageChange = async (event) => {
    setFirst(event.first);
    setRows(event.rows);
    setIsLoadingTable(true);
    const notification = toast.loading("Buscando..");
    try {
      // precisa colocar o endpoint do contas a pagar
      const response = await getAccountToPay(filter, event.page + 1);

      const obj = response.data.map((row) => ({
        ...row,
        block: row.sale.land.name + " / " + row.sale.land.block.name,
      }));

      setDataTable(obj);
      setSavedDataTable(obj);

      const totalRecords = response.last_page * response.per_page;
      setTotalRecords(totalRecords);
      setCurrentPage(response.current_page);

      toast.update(notification, {
        render: "Consulta realizada com sucesso",
        type: "success",
        theme: "colored",
        autoClose: 5000,
        isLoading: false,
      });
    } catch (error) {
      toast.update(notification, {
        render: "Falha ao realizar consulta!",
        type: "error",
        theme: "colored",
        autoClose: 5000,
        isLoading: false,
      });
    } finally {
      setIsLoadingTable(false);
    }
  };

  useEffect(() => {
    if (filter) {
      form.setValue("supplier", filter?.supplier);
      form.setValue("enterprise_id", filter?.enterprise_id);
      form.setValue("status", filter?.status);
      form.setValue("completed_automatic", filter?.completed_automatic);
      form.setValue("lawsuit", filter?.lawsuit);
      form.setValue("period_start", filter?.period_start);
      form.setValue("period_end", filter?.period_end);
      form.setValue("type_period", filter?.type_period);
      form.setValue("installment_id", filter?.installment_id);
      form.setValue("block_id", filter?.block_id);
      form.setValue("land_id", filter?.land_id);
      form.setValue("cost_center", filter?.cost_center);
      updatedTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //recebendo o centro de custo
  useEffect(() => {
    if (data) {
      //ordenando o centro de custo
      data.sort((a, b) => {
        const refA = a.ref.split(".").map(Number);
        const refB = b.ref.split(".").map(Number);

        // Comparar cada parte separadamente
        for (let i = 0; i < Math.max(refA.length, refB.length); i++) {
          const numA = refA[i] || 0; // Se não houver valor, assume 0
          const numB = refB[i] || 0;

          if (numA !== numB) {
            return numA - numB;
          }
        }

        return 0;
      });
      // console.log("centro de custo ordenado", data);
      const refactorData = data.map((item) => ({
        value: item.id,
        label: `${item.ref} - ${item.desc}`,
      }));
      // console.log("Centro de custo formatado:", refactorData);
      //definir somente que começa com 2 no centro de custo??? pois está aberto pro cliente colocar todos os centro como quer
      setCostCenter(refactorData);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      // Função para verificar se um item tem subcategorias
      function hasSubcategories(ref, costCenter) {
        return costCenter.some((item) => item.ref.startsWith(ref + "."));
      }
  
      // Filtra apenas os Centros de Custo que começam com "2"
      const filteredData = data.filter((item) => item.ref && item.ref.startsWith("2"));
  
      // Ordenação dos Centros de Custo
      const sortedData = filteredData.sort((a, b) => {
        const refA = a.ref.split(".").map(Number);
        const refB = b.ref.split(".").map(Number);
  
        for (let i = 0; i < Math.max(refA.length, refB.length); i++) {
          const numA = refA[i] || 0;
          const numB = refB[i] || 0;
          if (numA !== numB) return numA - numB;
        }
  
        return 0;
      });
  
      // Filtrando apenas os itens que **não são pais**
      const onlyChildren = sortedData.filter((item) => !hasSubcategories(item.ref, filteredData));
  
      // Formatando para o Select
      const refactoredData = onlyChildren.map((item) => ({
        value: item.id,
        label: `${item.ref} - ${item.desc}`,
      }));
  
      setCostCenter(refactoredData);
    }
  }, [data]);
  
  
  return (
    <>
      <div className="row d-flex justify-content-between mb-3">
  <AccountHeaderToPay updatedTable={updatedTable} filter={filter} enterpriseLogoBase64={enterpriseLogoBase64} />
        <Panel title="Informe o Período">
          <form
            className="row d-flex mt-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {/* {permissions &&
              permissions.includes("10") && ( //ver todos os fornecedores */}
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="supplier_id"
                control={form.control}
                label={isLoadingSupplier ? "Carregando..." : "Fornecedor"}
                disabled={isLoadingSupplier}
                options={supplier}
                errors={errors}
              />
            </div>
            {/* )} */}
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="enterprise_id"
                label={
                  isLoadingEnterprises ? "Carregando..." : "Empreendimento "
                }
                control={form.control}
                preValue={filter?.enterprise_id}
                disabled={isLoadingEnterprises}
                isRequired={false}
                onChange={(e) => selectEnterprise(e)}
                options={enterprise}
                errors={errors}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="block_id"
                label={
                  isLoadingBlocks ? "Carregando..." : nomenclature[0].value
                }
                control={form.control}
                disabled={isLoadingBlocks || !form.getValues("enterprise_id")}
                onChange={(e) => selectBlock(e)}
                options={blocks}
                errors={errors}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="land_id"
                label={isLoadingLands ? "Carregando..." : nomenclature[1].value}
                control={form.control}
                errors={errors}
                options={lands ? lands : ""}
                disabled={isLoadingLands || !form.getValues("block_id")}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="type_period"
                label="Tipo Período"
                control={form.control}
                options={typePeriodOptions}
                errors={errors}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="status"
                label="Situação"
                preValue={filter?.status}
                control={form.control}
                options={optionCurrent}
                errors={errors}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="current_account_id"
                label="Conta"
                control={form.control}
                options={currentAccount}
                errors={errors}
              />
            </div>
           
            <div className="col-md-4 col-xl-3 form-group">
              <Input
                name="completed_code"
                label="Nº Baixa"
                control={form.control}
                errors={errors}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Input
                name="id"
                label="Nº Lançamento"
                control={form.control}
                errors={errors}
              />
            </div>
           
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Select
                name="cost_center_id"
                label={isLoading ? "Carregando..." : "Centro de Custo"}
                control={form.control}
                errors={errors}
                options={cost_center ? cost_center : ""}
                disabled={isLoading}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <Input
                name="receipt"
                label="Código de Barras"
                control={form.control}
                errors={errors}
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <CalendarInput
                name="period_start"
                control={form.control}
                errors={errors}
                label="Data início"
              />
            </div>
            <div className="col-md-4 col-xl-3 mb-3 form-group">
              <CalendarInput
                name="period_end"
                control={form.control}
                errors={errors}
                label="Data Fim"
              />
            </div>
            <div className="d-flex justify-content-end my-2">
              <div
                className={`${
                  window.innerWidth <= 992 ? "w-25" : "col-md-1"
                } text-center`}
              >
                <Button
                  type="submit"
                  icon="fa fa-search"
                  aria-label="Search"
                  loading={loading}
                  className={`rounded-3 py-2.5 w-100`}
                />
              </div>
            </div>
          </form>
        </Panel>
        {dataTable?.length > 0 && (
          <Panel mainCss="mt-4">
            <TableAccountToPay
              dataTable={dataTable || []}
              totalRecords={totalRecords}
              pageTotals={pageTotals}
              isLoading={isLoading}
            />
            <div style={{ display: "flex", gap: "2rem", margin: "16px 0", flexWrap: "wrap" }}>
              <div><strong>Valor Total:</strong> {formatCurrency(totals.value || 0)}</div>
              <div><strong>Valor em Aberto:</strong> {formatCurrency(totals.open_value || 0)}</div>
              <div><strong>Valor Pago:</strong> {formatCurrency(totals.payment_value || 0)}</div>
              <div><strong>Acréscimos:</strong> {formatCurrency(totals.addition || 0)}</div>
              <div><strong>Descontos:</strong> {formatCurrency(totals.discount || 0)}</div>
              <div><strong>Parcelas:</strong> {totals.instalments || 0}</div>
            </div>
         
          </Panel>
        )}
      </div>
    </>
  );
}
